import React, { useState } from 'react';
import { Star, X } from 'lucide-react';
import { FOOD_REVIEW_URL } from '../../config/api';
import { authenticatedFetch } from '../Utils/apiHelpers';
import { trackFood, trackError } from '../Utils/analytics';
import './FoodReviewModal.css';

interface FoodReviewModalProps {
  foodId: number;
  foodName: string;
  onClose: () => void;
  showNotification: (type: 'success' | 'error' | 'info' | 'warning', title: string, message: string) => void;
}

const FoodReviewModal: React.FC<FoodReviewModalProps> = ({ foodId, foodName, onClose, showNotification }) => {
  const [rating, setRating] = useState(0);
  const [hoveredRating, setHoveredRating] = useState(0);
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (rating === 0) {
      showNotification('warning', 'Rating Required', 'Please select a star rating before submitting.');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await authenticatedFetch(FOOD_REVIEW_URL(foodId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ rating, comment: comment.trim() }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.success) {
        showNotification('success', 'Review Submitted', `Thank you for rating ${foodName}!`);
        trackFood('food_reviewed', foodName);
        onClose();
      } else {
        const msg = data.message || data.detail || 'Failed to submit review. Please try again.';
        showNotification('error', 'Review Failed', msg);
        trackError('food_review_failed', 'food_review_modal');
      }
    } catch {
      showNotification('error', 'Network Error', 'Please check your connection and try again.');
      trackError('food_review_network_error', 'food_review_modal');
    } finally {
      setIsSubmitting(false);
    }
  };

  const displayRating = hoveredRating || rating;

  return (
    <div className="frm-overlay" onClick={onClose}>
      <div className="frm-modal" onClick={e => e.stopPropagation()}>
        <button className="frm-close" onClick={onClose} aria-label="Close">
          <X size={20} />
        </button>

        <div className="frm-header">
          <h3 className="frm-title">Rate This Dish</h3>
          <p className="frm-food-name">{foodName}</p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="frm-stars-section">
            <p className="frm-stars-label">Your rating</p>
            <div className="frm-stars" role="group" aria-label="Star rating">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  className={`frm-star ${n <= displayRating ? 'frm-star--filled' : ''}`}
                  onClick={() => setRating(n)}
                  onMouseEnter={() => setHoveredRating(n)}
                  onMouseLeave={() => setHoveredRating(0)}
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                >
                  <Star size={32} fill={n <= displayRating ? 'currentColor' : 'none'} />
                </button>
              ))}
            </div>
            {displayRating > 0 && (
              <span className="frm-rating-label">
                {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][displayRating]}
              </span>
            )}
          </div>

          <div className="frm-comment-section">
            <label htmlFor="frm-comment" className="frm-comment-label">
              Comment <span className="frm-optional">(optional)</span>
            </label>
            <textarea
              id="frm-comment"
              className="frm-comment"
              value={comment}
              onChange={e => setComment(e.target.value)}
              placeholder="Share your experience with this dish..."
              rows={4}
              maxLength={500}
            />
            <span className="frm-char-count">{comment.length}/500</span>
          </div>

          <div className="frm-actions">
            <button type="button" className="frm-btn frm-btn--cancel" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </button>
            <button type="submit" className="frm-btn frm-btn--submit" disabled={isSubmitting || rating === 0}>
              {isSubmitting ? (
                <><span className="frm-spinner" />Submitting...</>
              ) : (
                'Submit Review'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FoodReviewModal;
